import { ScrollMaps, ScrollMapsHost } from '../../page-insights/scrollmaps';
import { OverlayMode } from '../../overlays/overlay';
import { WebPageRenderer } from '../renderer';
import { ScrollMapDisplayOptions, ScrollMapsFacet } from '@fullstory/page-insights-ui';
import { findFirstInternalScrollNode } from '../../scrollmapUtils';

export class WebScrollMaps implements ScrollMaps, OverlayMode {
  private _scroll: (e: Event) => void;
  private _scrollElements: (Element | Window)[];

  constructor(private _renderer: WebPageRenderer, private _host: ScrollMapsHost) {}

  start() {}

  stop() {
    this.clear();
  }

  clear() {
    this._renderer.overlays().clearScrollMaps();
    this._unhookScroll();
  }

  placeScrollMaps(scrolls: ScrollMapsFacet, displayOptions: ScrollMapDisplayOptions) {
    this.clear();
    if (scrolls.totalPageViews == 0) {
      this._host.renderStats(scrolls); // Send it back out to render an error message.
      return; // Return immediately rather than trying to display the gradient or watermark when no data was found.
    }

    const frame = this._renderer.activeRootDocFrame();
    if (!frame) {
      return;
    }
    this._renderer
      .overlays()
      .placeScrollMaps(scrolls, displayOptions, frame, this._renderer.view().scaled);

    this._scrollElements = [frame.contentWindow];
    const internalScrollMapElement: Element = findFirstInternalScrollNode(
      frame?.contentDocument,
      scrolls.internalScrollSelectors,
    );
    let scrollElementHeight: number;

    if (internalScrollMapElement) {
      this._scrollElements.push(internalScrollMapElement);
      scrollElementHeight = Math.max(
        internalScrollMapElement.scrollHeight,
        internalScrollMapElement.getBoundingClientRect().height,
      );
    } else {
      scrollElementHeight = Math.max(
        frame.contentDocument.scrollingElement.scrollHeight,
        frame.contentDocument.documentElement.getBoundingClientRect().height,
      );
    }

    this._host.renderStats(scrolls, scrollElementHeight); // Send it back out.

    this._scroll = () => {
      this._renderer.overlays().layoutScrollMaps();
    };

    // For internal scroll elements, we need to listen for both the window and element scrolls since it's possible
    // the internal scroll element bounds could with the header or footer being scroll out.
    this._scrollElements.forEach(item => item.addEventListener('scroll', this._scroll, true));
  }

  togglePageFold(checked: boolean) {
    this._renderer.overlays().togglePageFoldDataDisplay(checked);
    this._renderer.overlays().layoutScrollMaps();
  }

  private _unhookScroll() {
    if (this._scroll) {
      const frame = this._renderer.activeRootDocFrame();
      if (!frame) {
        return;
      }
      this._scrollElements.forEach(item => item.removeEventListener('scroll', this._scroll, true));
    }
  }
}
